import React, { useState, useEffect, useRef } from 'react';
import { KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import { OTP_TEXT, otpChannelLabel, otpChannelOf, otpSentText, postJsonWithOtpCapability, useOtpDelivery } from '../lib/otpChannel';
import TwoFactor from '../components/TwoFactor';

// Cloudflare-Turnstile-Site-Key (öffentlich). Ist er gesetzt, erscheint die Bot-Challenge.
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const API = process.env.REACT_APP_API_URL || 'https://localhost:5001/api';

// Eigener fetch (umgeht den globalen 401-Interceptor von services/api, der sonst bei falschem
// Reset-Code zum Login umleiten würde).
// WHATSAPP-OTP-STANDARD-001: Code-auslösende Pfade tragen das Token (Liste + Sicherheitsnetz in
// lib/otpChannel.js); `channel` nur aus Antworten, deren Anfrage es trug.
async function sendJson(path, body) {
  const controller = new AbortController();
  const deadline = setTimeout(() => controller.abort(), 15000);
  try {
    const { response, data, advertised } = await postJsonWithOtpCapability({
      fetchImpl: (url, options) => fetch(url, { ...options, signal: controller.signal }),
      base: API, path, body, headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
    });
    if (controller.signal.aborted) throw new Error('timeout');
    if (!response.ok) throw new Error(data.error || data.message || 'Fehler');
    return { data, channel: otpChannelOf(data, advertised) };
  } catch (error) {
    if (controller.signal.aborted) throw new Error('Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.');
    throw error;
  } finally { clearTimeout(deadline); }
}

async function postJson(path, body) {
  return (await sendJson(path, body)).data;
}

// Login-Zwischenschritte laufen bewusst ohne den globalen Axios-Interceptor: Das kurzlebige
// Setup-Token darf nie als normale Admin-Sitzung in localStorage landen.
async function postAdminEnrollment(body) {
  const response = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || data.error || 'Anmeldung fehlgeschlagen');
    error.response = { status: response.status, data };
    throw error;
  }
  return data;
}

// Ziel einer Telefon-Zustellung in der Meldung. Die Antwort von /auth/forgot/request ist absichtlich
// konstant (Scan 26.08., M-1: verrät nicht, ob es das Konto gibt): Ohne `via` trägt `dest` die maskierte
// E-MAIL-Adresse, `phone` ist immer null — die Nummer selbst gibt der Server nie heraus. Eine E-Mail-
// Adresse ist nie das Ziel eines SMS-/WhatsApp-Codes, also dann neutral „deine hinterlegte Nummer".
export function phoneTargetLabel(response) {
  const dest = typeof response?.dest === 'string' ? response.dest.trim() : '';
  return dest && !dest.includes('@') ? dest : 'deine hinterlegte Nummer';
}

const inputStyle = { width: '100%', padding: '12px 14px', background: '#000', border: '1px solid #333', borderRadius: 8, fontSize: 14, color: '#fff', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 };

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '', totp: '' });
  const [loading, setLoading] = useState(false);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [enrollment, setEnrollment] = useState(null); // { email, code, deliveryFailed }
  const [setupToken, setSetupToken] = useState(null);
  const [mode, setMode] = useState('login');            // 'login' | 'forgot'
  const { login } = useAuth();
  const navigate = useNavigate();
  const attemptRef = useRef(0);
  useEffect(() => {
    attemptRef.current += 1; setLoading(false);
    return () => { attemptRef.current += 1; };
  }, [mode]);

  useEffect(() => {
    if (!SITE_KEY || document.querySelector('script[src*="turnstile"]')) return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true; s.defer = true;
    document.head.appendChild(s);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const tsToken = SITE_KEY ? (document.querySelector('[name="cf-turnstile-response"]')?.value || '') : '';
    if (SITE_KEY && !tsToken) { toast.error('Bitte die Bot-Prüfung abschließen.'); return; }
    setLoading(true);
    const attempt = ++attemptRef.current;
    const current = () => attempt === attemptRef.current;
    try {
      if (enrollment) {
        const proof = enrollment.code || undefined;
        await postAdminEnrollment({
          email: form.email, password: form.password, role: 'admin', turnstile_token: tsToken,
          mfa_enrollment_code: enrollment.deliveryFailed ? undefined : proof,
          recovery_code: proof,
        });
        return;
      }
      if (await login(form.email, form.password, form.totp, tsToken, current) === false || !current()) return;
      toast.success('Willkommen, Admin!');
      navigate('/');
    } catch (err) {
      if (!current()) return;
      if (err.response?.data?.error === '2fa_required') {
        setNeedsTotp(true);
        toast('Bitte Authenticator- oder Recovery-Code eingeben.', { icon: <KeyRound size={18} /> });
      } else if (err.response?.data?.error === 'mfa_enrollment_required') {
        setEnrollment({ email: err.response.data.email || 'deine hinterlegte E-Mail-Adresse', code: '', deliveryFailed: false });
        toast('Bestätige zuerst den Sicherheitscode aus deiner E-Mail.', { icon: <KeyRound size={18} /> });
      } else if (err.response?.data?.error === 'mfa_enrollment_delivery_failed') {
        setEnrollment({ email: null, code: '', deliveryFailed: true });
        toast('Der E-Mail-Code konnte nicht zugestellt werden. Nutze einen vorhandenen Recovery-Code.', { icon: <KeyRound size={18} /> });
      } else if (err.response?.data?.error === 'mfa_setup_required' && typeof err.response.data.setup_token === 'string') {
        setSetupToken(err.response.data.setup_token);
        setEnrollment(null);
      } else {
        toast.error(err.message || err.response?.data?.error || 'Anmeldung fehlgeschlagen');
      }
    } finally {
      if (current()) {
        setLoading(false);
        // Turnstile-Token sind Einweg: nach jedem Login-Versuch (auch dem 2FA-Zwischenschritt)
        // muss die Challenge neu laufen, sonst lehnt der Server mit timeout-or-duplicate ab.
        if (SITE_KEY) window.turnstile?.reset?.();
      }
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 20 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h1 style={{ color: '#fff', fontSize: 44, fontWeight: 900, letterSpacing: -2, marginBottom: 4 }}>V<span style={{ color: '#9C27B0' }}>OO</span>GO</h1>
          <p style={{ color: '#444', fontSize: 13 }}>Admin-Verwaltung</p>
        </div>
        <div style={{ background: '#111', borderRadius: 18, padding: 28, border: '1px solid #222' }}>
          {mode === 'login' ? (
            <>
              {setupToken ? (
                <div style={{ background: '#fff', borderRadius: 14 }}>
                  <TwoFactor required setupToken={setupToken} onComplete={() => navigate(0)} />
                </div>
              ) : <>
              <h2 style={{ fontSize: 16, fontWeight: 800, marginBottom: 20, color: '#fff' }}>{enrollment ? 'Sicherheitscode bestätigen' : 'Anmelden'}</h2>
              {enrollment && <p style={{ color: '#aaa', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
                {enrollment.deliveryFailed
                  ? 'Der Sicherheitscode konnte nicht per E-Mail zugestellt werden. Du kannst einen bereits vorhandenen Recovery-Code eingeben.'
                  : <>Wir haben einen Code an {enrollment.email} geschickt. Alternativ kannst du einen vorhandenen Recovery-Code eingeben.</>}
              </p>}
              <form onSubmit={submit}>
                {!enrollment && <>
                {[{ key: 'email', label: 'Admin E-Mail', type: 'email', ph: 'admin@voogo.de' }, { key: 'password', label: 'Passwort', type: 'password', ph: '••••••••' }].map(({ key, label, type, ph }) => (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>{label}</label>
                    <input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} placeholder={ph} required
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#fff'} onBlur={e => e.target.style.borderColor = '#333'} />
                  </div>
                ))}
                {needsTotp && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ ...labelStyle, color: '#9C27B0' }}>Authenticator- oder Recovery-Code</label>
                    <input type="text" autoFocus value={form.totp}
                      onChange={e => setForm({ ...form, totp: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9) })} placeholder="123456 oder XXXX-XXXX" required
                      style={{ ...inputStyle, border: '1px solid #9C27B0', fontSize: 16, letterSpacing: 3, textAlign: 'center' }} />
                  </div>
                )}
                </>}
                {enrollment && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ ...labelStyle, color: '#9C27B0' }}>{enrollment.deliveryFailed ? 'Recovery-Code' : 'E-Mail- oder Recovery-Code'}</label>
                    <input type="text" autoFocus value={enrollment.code}
                      onChange={e => setEnrollment({ ...enrollment, code: e.target.value.trim() })}
                      placeholder={enrollment.deliveryFailed ? 'XXXX-XXXX' : '123456 oder XXXX-XXXX'} required
                      style={{ ...inputStyle, border: '1px solid #9C27B0', textAlign: 'center' }} />
                  </div>
                )}
                {SITE_KEY && <div className="cf-turnstile" data-sitekey={SITE_KEY} data-theme="dark" style={{ marginBottom: 14 }} />}
                <button type="submit" disabled={loading}
                  style={{ width: '100%', padding: '13px', marginTop: 6, background: loading ? '#222' : '#fff', color: '#000', borderRadius: 8, fontSize: 16, fontWeight: 900, letterSpacing: -0.3, border: 'none', cursor: 'pointer' }}>
                  {loading ? 'Wird geprüft...' : (enrollment ? 'Code bestätigen' : 'Anmelden')}
                </button>
              </form>
              {!enrollment && <button onClick={() => { setMode('forgot'); }}
                style={{ marginTop: 16, background: 'none', border: 'none', color: '#666', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                Passwort vergessen?
              </button>}
              {enrollment && <button onClick={() => { setEnrollment(null); setSetupToken(null); }}
                style={{ marginTop: 16, background: 'none', border: 'none', color: '#666', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                Zurück zur Anmeldung
              </button>}
              </>}
            </>
          ) : (
            <ForgotPassword email={form.email} onBack={() => setMode('login')} />
          )}
        </div>
        <p style={{ textAlign: 'center', marginTop: 16, color: '#333', fontSize: 12 }}>
          VOOGO UG (haftungsbeschränkt) · Admin-Panel
        </p>
      </div>
    </div>
  );
}

// ── Passwort vergessen: Schritt 1 (E-Mail → Wege) → Schritt 2 (Faktor + neues Passwort) ──
function ForgotPassword({ email: initialEmail, onBack }) {
  const [step, setStep] = useState('email');           // 'email' | 'verify'
  const [email, setEmail] = useState(initialEmail || '');
  const [channels, setChannels] = useState([]);
  const [phone, setPhone] = useState(null);
  const [totp, setTotp] = useState('');
  const [recovery, setRecovery] = useState('');
  const [sms, setSms] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const active = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const otp = useOtpDelivery();   // WHATSAPP-OTP-STANDARD-001: wohin der Telefon-Code ging

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: r, channel } = await sendJson('/auth/forgot/request', { email });
      if (!active.current) return;
      const availableChannels = Array.isArray(r.channels)
        ? r.channels.filter(channel => ['totp', 'recovery', 'sms', 'email'].includes(channel))
        : [];
      const hasAlternative = availableChannels.some(channel => ['totp', 'recovery', 'email'].includes(channel));
      if (r.sent !== true && !hasAlternative) {
        toast.error('Der Bestätigungscode konnte nicht zugestellt werden.');
        return;
      }
      setChannels(availableChannels); setPhone(r.phone || null);
      otp.receive(r.sent === true ? channel : null);
      if (r.sent === true) {
        toast.success(`Code per ${otpChannelLabel(channel)} an ${phoneTargetLabel(r)} gesendet.`);
      } else if (availableChannels.includes('totp') || availableChannels.includes('recovery')) {
        toast('Nutze deinen Authenticator-, Recovery- oder E-Mail-Weg.');
      } else {
        toast('Wähle „Code per E-Mail“, um einen Bestätigungscode anzufordern.');
      }
      setStep('verify');
    } catch (err) { if (!active.current) return; toast.error(err.message || 'Fehler'); }
    finally { if (active.current) setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Neues Passwort: mindestens 8 Zeichen.'); return; }
    setBusy(true);
    try {
      await postJson('/auth/forgot/verify', {
        email, newPassword,
        totp: totp || undefined, recovery_code: recovery || undefined, code: sms || undefined,
      });
      if (!active.current) return;
      toast.success('Passwort gesetzt. Bitte jetzt anmelden.');
      onBack();
    } catch (err) { if (!active.current) return; toast.error(err.message || 'Kein gültiger Code.'); }
    finally { if (active.current) setBusy(false); }
  };

  // Code gezielt per SMS oder E-Mail (neu) senden. `preferSms`: „Lieber per SMS" — dieselbe Anfrage
  // mit channel:'sms', damit der noch gültige WhatsApp-Code einmal per SMS kommt.
  const resendVia = async (via, { preferSms = false } = {}) => {
    setBusy(true);
    try {
      const { data: r, channel } = await sendJson('/auth/forgot/request', preferSms ? { email, via, channel: 'sms' } : { email, via });
      if (!active.current) return false;
      if (r.sent === true) {
        otp.receive(via === 'email' ? null : channel);
        toast.success(via === 'email' ? `Code per E-Mail an ${r.dest} gesendet.` : `Code per ${otpChannelLabel(channel)} an ${phoneTargetLabel(r)} gesendet.`);
        return true;
      }
      toast.error(via === 'email' ? 'E-Mail-Versand ist (noch) nicht verfügbar.' : 'SMS-Versand ist (noch) nicht verfügbar.');
    } catch (err) { if (!active.current) return; toast.error(err.message || 'Fehler'); }
    finally { if (active.current) setBusy(false); }
    return false;
  };
  const preferSms = async () => {
    if (busy || !otp.beginSmsSwitch()) return;   // Doppelklick-Schutz
    if (!(await resendVia('sms', { preferSms: true }))) otp.failSmsSwitch();
  };

  const has = (c) => channels.includes(c);
  const showAll = channels.length === 0;   // unbekannt → alle Felder anbieten

  return (
    <>
      <h2 style={{ fontSize: 16, fontWeight: 800, marginBottom: 8, color: '#fff' }}>Passwort zurücksetzen</h2>
      {step === 'email' ? (
        <form onSubmit={request}>
          <p style={{ color: '#888', fontSize: 12, lineHeight: 1.6, marginBottom: 16 }}>
            Gib deine Admin-E-Mail ein. Du bestätigst danach mit deinem Authenticator-Code, einem Recovery-Code oder (falls aktiv) einem Code an deine Telefonnummer.
          </p>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Admin E-Mail</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@voogo.de" required autoFocus style={inputStyle} />
          </div>
          <button type="submit" disabled={busy} style={{ width: '100%', padding: '13px', background: '#9C27B0', color: '#000', borderRadius: 8, fontSize: 16, fontWeight: 900, border: 'none', cursor: 'pointer' }}>
            {busy ? 'Bitte warten…' : 'Weiter'}
          </button>
        </form>
      ) : (
        <form onSubmit={verify}>
          {(has('totp') || showAll) && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ ...labelStyle, color: '#9C27B0' }}>Authenticator-Code</label>
              <input inputMode="numeric" maxLength={6} value={totp} onChange={e => setTotp(e.target.value.replace(/\D/g, ''))} placeholder="123456"
                style={{ ...inputStyle, letterSpacing: 4, textAlign: 'center' }} />
            </div>
          )}
          {(has('recovery') || showAll) && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Recovery-Code</label>
              <input value={recovery} onChange={e => setRecovery(e.target.value.toUpperCase())} placeholder="XXXX-XXXX" style={inputStyle} />
            </div>
          )}
          {(has('sms') || has('email') || showAll) && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Code per WhatsApp/SMS oder E-Mail</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {(has('sms') || showAll) && (otp.showsPreferSms ? (
                  // WHATSAPP-OTP-STANDARD-001: Liegt ein WhatsApp-Code vor, ist der Telefon-Knopf „Lieber per SMS".
                  <button type="button" onClick={preferSms} disabled={busy || otp.switching} title={OTP_TEXT.preferSmsHint}
                    style={{ flex: 1, padding: '9px', background: '#0a0a0a', color: '#fff', border: '1px solid #333', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{OTP_TEXT.preferSms}</button>
                ) : (
                  <button type="button" onClick={() => resendVia('sms')} disabled={busy}
                    style={{ flex: 1, padding: '9px', background: '#0a0a0a', color: '#fff', border: '1px solid #333', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Code</button>
                ))}
                {(has('email') || showAll) && (
                  <button type="button" onClick={() => resendVia('email')} disabled={busy}
                    style={{ flex: 1, padding: '9px', background: '#0a0a0a', color: '#fff', border: '1px solid #333', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Code per E-Mail</button>
                )}
              </div>
              {otp.channel && (
                <p style={{ color: '#888', fontSize: 12, lineHeight: 1.6, margin: '0 0 8px' }}>{otpSentText(otp.channel)}</p>
              )}
              <input inputMode="numeric" maxLength={6} value={sms} onChange={e => setSms(e.target.value.replace(/\D/g, ''))} placeholder="Code eingeben"
                style={{ ...inputStyle, letterSpacing: 4, textAlign: 'center' }} />
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Neues Passwort (min. 8 Zeichen)</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} />
          </div>
          <button type="submit" disabled={busy} style={{ width: '100%', padding: '13px', background: '#9C27B0', color: '#000', borderRadius: 8, fontSize: 16, fontWeight: 900, border: 'none', cursor: 'pointer' }}>
            {busy ? 'Bitte warten…' : 'Passwort setzen'}
          </button>
        </form>
      )}
      <button onClick={onBack} style={{ marginTop: 16, background: 'none', border: 'none', color: '#666', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
        ← Zurück zur Anmeldung
      </button>
    </>
  );
}
